import { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

// Interface for project data
export interface Project {
  id: string;
  projectName: string;
  projectDescription?: string;
  companyId?: string;
  [key: string]: any; // Allow additional properties
}

export const useProjects = (autoFetch: boolean = true) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const projectsCollection = collection(db, 'projects');
      const projectsSnapshot = await getDocs(projectsCollection);
      
      const projectsData: Project[] = [];
      projectsSnapshot.forEach((doc) => {
        const data = doc.data();
        projectsData.push({
          id: doc.id,
          projectName: data.projectName || '',
          projectDescription: data.projectDescription || '',
          companyId: data.companyId || '',
          ...data
        } as Project);
      });
      
      // Sort projects by name
      projectsData.sort((a, b) => a.projectName.localeCompare(b.projectName));
      
      setProjects(projectsData);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('❌ useProjects: Error fetching projects:', err);
      }
      setError('Failed to load projects');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Only fetch projects when hook is first used if autoFetch is true
  useEffect(() => {
    if (autoFetch) {
      fetchProjects();
    }
  }, [fetchProjects, autoFetch]);

  const getProjectById = useCallback(async (projectId: string): Promise<Project | null> => {
    try {
      const projectRef = doc(db, 'projects', projectId);
      const projectDoc = await getDoc(projectRef);
      
      if (!projectDoc.exists()) {
        return null;
      }
      
      const data = projectDoc.data();
      return {
        id: projectDoc.id,
        projectName: data.projectName || '',
        projectDescription: data.projectDescription || '',
        companyId: data.companyId || '',
        ...data
      } as Project;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('❌ useProject: Error fetching project by ID:', error);
      }
      return null;
    }
  }, []);

  return {
    projects,
    loading,
    error,
    refetch: fetchProjects,
    getProjectById
  };
};

